import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { take } from 'rxjs';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { SolPropertiesProposalOptionalPageSelectionComponent } from "../../../../dialogs/sol/sol-properties-proposal-optional-page-selection/sol-properties-proposal-optional-page-selection.component";
import { MatButtonModule } from "@angular/material/button";
import { SolPropertiesProposalAdvancedOptionsComponent } from "../../../../dialogs/sol/sol-properties-proposal-advanced-options/sol-properties-proposal-advanced-options.component";
import { TranslatePipe } from "@ngx-translate/core";
import { ProposalSettings } from "../../../../../models/dtos/Classes/ProposalSettings";
import { SolPropertiesService } from "../../../../../services/properties/sol-properties.service";
import { AreaSummaryType, DropDownItem, PricingSummaryType, PricingType } from "../../../../../models/dtos";
import { IProposalFormGroup } from "../../../../../models/properties/IProposalFormGroup";
import { CommonDataService } from "../../../../../services/common-data.service";
import { CONSTANTS } from "../../../../../Constants/Constants";

@Component({
    selector: 'app-sol-properties-proposal',
    templateUrl: './sol-properties-proposal.component.html',
    styleUrls: ['./sol-properties-proposal.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatCheckboxModule,
        MatRadioModule,
        MatButtonModule,
        TranslatePipe
    ]
})
export class SolPropertiesProposalComponent implements OnInit {
    proposalForm!: FormGroup<IProposalFormGroup>;
    proposalSettings!: ProposalSettings;
    isReadOnly = false;
    materialDropDownList: DropDownItem[];
    laborDropDownList: DropDownItem[];
    subcontractorsDropDownList: DropDownItem[];
    miscellaneousDropDownList: DropDownItem[];
    travelDropDownList: DropDownItem[];
    priceUnderCategoriesDropDownList: DropDownItem[];
    materialListDropDownList: DropDownItem[];
    identifierDropDownList: DropDownItem[];
    private destroyRef = inject(DestroyRef);
    pricingSummaryType = PricingSummaryType;
    areaSummaryType = AreaSummaryType;
    private readonly USER_DEFINED_INSTALL_TYPE_ID = 7;
    private readonly NO_TAX_TYPE_ID = 3;

    constructor(
        private readonly matDialog: MatDialog,
        private readonly fb: NonNullableFormBuilder,
        private readonly solPropertiesService: SolPropertiesService,
        private readonly commonDataService: CommonDataService
    ) {}

    ngOnInit(): void {
        this.initializeForm();
        this.subscribeFormValueChanges();
        this.setupReadOnlyMode();
    }

    private subscribeFormValueChanges(): void {
        this.proposalForm.valueChanges
        .pipe(take(CONSTANTS.ONE))
        .subscribe(
            () => this.solPropertiesService.markPropertiesChanged()
        );
    }

    private setupReadOnlyMode(): void {
        this.commonDataService.readOnlyMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isReadOnly => {
                this.isReadOnly = this.solPropertiesService.isProposalReadOnly(isReadOnly);
                if (this.isReadOnly) {
                    this.proposalForm.disable({ emitEvent: false });
                } else {
                    this.proposalForm.enable({ emitEvent: false });
                    this.applyInitialDisabledStates();
                    this.syncProposalSettingsFromForm();
                }
            });
    }

    getInitialData(): void{
          this.solPropertiesService.getProposalMaterialsMasterData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => { this.materialDropDownList = data; });
        this.solPropertiesService.getProposalLaborMasterData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => { this.laborDropDownList = data; });
        this.solPropertiesService.getProposalSubcontractorsMasterData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => { this.subcontractorsDropDownList = data; });
        this.solPropertiesService.getProposalMiscellaneousMasterData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => { this.miscellaneousDropDownList = data; });
        this.solPropertiesService.getProposalTravelsMasterData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => { this.travelDropDownList = data; });
        this.solPropertiesService.getPriceUnderCategoriesMasterData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => { this.priceUnderCategoriesDropDownList = data; });
        this.solPropertiesService.getProposalIdentifierMasterData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => { this.identifierDropDownList = data; });
    }

    initializeForm(): void {
        this.proposalSettings = this.solPropertiesService.getOtherMetaDataModel().proposalSettings;
        this.getInitialData();
        this.proposalForm = this.fb.group<IProposalFormGroup>({
            projectPricingOption: this.fb.control<number>(this.proposalSettings?.pricingSummaryType ?? PricingSummaryType.SellPriceOnly),
            areaPricingOption: this.fb.control<number>(this.proposalSettings?.areaSummaryType ?? AreaSummaryType.None),
            materialDropdownOption: this.fb.control<number>(this.proposalSettings.materialDetailOption),
            sAndH: this.fb.control<boolean>(this.proposalSettings.sh),
            warranty: this.fb.control<boolean>(this.proposalSettings.warranty),
            useTax: this.fb.control<boolean>(this.proposalSettings.useTax),
            laborDropdownOption: this.fb.control<number>(this.proposalSettings.laborDetailOption),
            pm: this.fb.control<boolean>(this.proposalSettings.pm),
            eng: this.fb.control<boolean>(this.proposalSettings.eng),
            ceng: this.fb.control<boolean>(this.proposalSettings.ceng),
            eeng: this.fb.control<boolean>(this.proposalSettings.eeng),
            leng: this.fb.control<boolean>(this.proposalSettings.leng),
            neng: this.fb.control<boolean>(this.proposalSettings.neng),
            qeng: this.fb.control<boolean>(this.proposalSettings.qeng),
            grap: this.fb.control<boolean>(this.proposalSettings.grap),
            prog: this.fb.control<boolean>(this.proposalSettings.prog),
            dds: this.fb.control<boolean>(this.proposalSettings.dds),
            mech: this.fb.control<boolean>(this.proposalSettings.mech),
            spec: this.fb.control<boolean>(this.proposalSettings.spec),
            elec: this.fb.control<boolean>(this.proposalSettings.elec),
            subcontractorsDropdownOption: this.fb.control<number>(this.proposalSettings.subcontractorsDetailOption),
            otherWarranty: this.fb.control<boolean>(this.proposalSettings.otherWarranty),
            otherSAndH: this.fb.control<boolean>(this.proposalSettings.otherSH),
            miscellaneousDropdownOption: this.fb.control<number>(this.proposalSettings.miscellaneousDetailOption),
            tax: this.fb.control<boolean>(this.proposalSettings.otherTax),
            travelDropdownOption: this.fb.control<number>(this.proposalSettings.travelDetailOption),
            includeExternalNotes: this.fb.control<boolean>(this.proposalSettings.includeExternalNotes),
            showAllUnselectedPricing: this.fb.control<boolean>(this.proposalSettings.showAllunselected),
            showAllUnselectedPricingDropdownOption: this.fb.control<number>(this.proposalSettings.showAllunselectedOption),
            materialListOption: this.fb.control<number>(this.proposalSettings.materialListOption),
            materialListIdentifier: this.fb.control<number>(this.proposalSettings.identifierOption),
            sellPrice: this.fb.control<boolean>(this.proposalSettings.withSellPrice),
            genericName: this.fb.control<boolean>(this.proposalSettings.genericName)
        });

        this.applyInitialDisabledStates();
        this.updateMaterialListOptions(this.proposalForm.controls.areaPricingOption.value, false);
        this.registerFormChangeHandlers();
        this.syncProposalSettingsFromForm();
    }

    private registerFormChangeHandlers(): void {
        this.proposalForm.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.syncProposalSettingsFromForm());
    }

    private syncProposalSettingsFromForm(): void {
        const ps = this.proposalSettings;
        const values = this.proposalForm.getRawValue();

        ps.pricingSummaryType = values.projectPricingOption;
        ps.areaSummaryType = values.areaPricingOption;
        ps.pricingType = this.getPricingType(values.projectPricingOption, values.areaPricingOption);
        ps.materialDetailOption = values.materialDropdownOption;
        ps.sh = values.sAndH;
        ps.warranty = values.warranty;
        ps.useTax = values.useTax;
        ps.laborDetailOption = values.laborDropdownOption;
        ps.pm = values.pm;
        ps.eng = values.eng;
        ps.ceng = values.ceng;
        ps.eeng = values.eeng;
        ps.leng = values.leng;
        ps.neng = values.neng;
        ps.qeng = values.qeng;
        ps.grap = values.grap;
        ps.prog = values.prog;
        ps.dds = values.dds;
        ps.mech = values.mech;
        ps.spec = values.spec;
        ps.elec = values.elec;
        ps.subcontractorsDetailOption = values.subcontractorsDropdownOption;
        ps.otherWarranty = values.otherWarranty;
        ps.otherSH = values.otherSAndH;
        ps.miscellaneousDetailOption = values.miscellaneousDropdownOption;
        ps.otherTax = values.tax;
        ps.travelDetailOption = values.travelDropdownOption;
        ps.includeExternalNotes = values.includeExternalNotes;
        ps.showAllunselected = values.showAllUnselectedPricing;
        ps.showAllunselectedOption = values.showAllUnselectedPricingDropdownOption;
        ps.materialListOption = values.materialListOption;
        ps.identifierOption = values.materialListIdentifier;
        ps.withSellPrice = values.sellPrice;
        ps.genericName = values.genericName;
    }

    private getPricingType(projectPricingOption: number, areaPricingOption: number): number {
        switch (projectPricingOption) {
            case PricingSummaryType.SellPriceOnly:
                switch (areaPricingOption) {
                    case AreaSummaryType.None:
                        return PricingType.SellPriceOnly;
                    case AreaSummaryType.SellPriceOnly:
                        return PricingType.SellPriceOnlyByArea;
                    case AreaSummaryType.PricingBreakout:
                        return PricingType.SellPriceOnlyPricingBreakoutByArea;
                    default:
                        return PricingType.NoPricingSummary;
                }

            case PricingSummaryType.PricingBreakout:
                switch (areaPricingOption) {
                    case AreaSummaryType.None:
                        return PricingType.PricingBreakout;
                    case AreaSummaryType.PricingBreakout:
                        return PricingType.PricingBreakoutByArea;
                    case AreaSummaryType.SellPriceOnly:
                        return PricingType.PricingBreakoutSellPriceOnlyByArea;
                    default:
                        return PricingType.NoPricingSummary;
                }

            case PricingSummaryType.None:
                switch (areaPricingOption) {
                    case AreaSummaryType.SellPriceOnly:
                        return PricingType.AreaSellPriceOnly;
                    case AreaSummaryType.PricingBreakout:
                        return PricingType.AreaPricingBreakoutOnly;
                    case AreaSummaryType.None:
                        return PricingType.NoPricingSummary;
                    default:
                        return PricingType.NoPricingSummary;
                }

            default:
                return PricingType.NoPricingSummary;
        }
    }

    private applyInitialDisabledStates(): void {
        this.updatePricingBreakoutState();
        this.updateMaterialListState();
        const estimateData = this.solPropertiesService.getEstimateData();
        const estimateSettings = this.solPropertiesService.getEstimateSettings();
        this.updateTaxCheckboxesBasedOnInstallAndTaxType(estimateSettings.installTypeID, estimateData.taxTypeID);
    }

    onPricingOptionChange(): void {
        this.updatePricingBreakoutState();
        this.updateMaterialListOptions(this.proposalForm.controls.areaPricingOption.value, true);
        const estimateData = this.solPropertiesService.getEstimateData();
        const estimateSettings = this.solPropertiesService.getEstimateSettings();
        this.updateTaxCheckboxesBasedOnInstallAndTaxType(estimateSettings.installTypeID, estimateData.taxTypeID);
        this.syncProposalSettingsFromForm();
    }

    onMaterialDropdownChange(): void {
        this.updateMaterialCheckboxesState();
        this.updateOtherCheckboxesState();
        this.syncProposalSettingsFromForm();
    }

    onLaborDropdownChange(): void {
        this.updateLaborCheckboxesState(false);
        this.syncProposalSettingsFromForm();
    }

    onMaterialIncludeCheckboxChange(): void {
        this.updateOtherCheckboxesState();
        this.syncProposalSettingsFromForm();
    }

    onShowAllUnselectedPricingChange(): void {
        this.updateShowAllUnselectedState();
        this.syncProposalSettingsFromForm();
    }

    onShowAllUnselectedPricingDropdownChange(): void {
        // this.proposalForm.controls.laborDropdownOption.setValue(2);
        // this.proposalForm.controls.laborDropdownOption.disable();
        this.updateUnselectedPricingCategoryState();
        this.syncProposalSettingsFromForm();
        
        // let showAllUnselectedPricingDropdownOption = this.proposalForm.controls.showAllUnselectedPricingDropdownOption.value;
        // if(showAllUnselectedPricingDropdownOption === 2){
        //     this.proposalForm.controls.spec.setValue(true);
        //     this.proposalForm.controls.spec.disable();
        // }else if(showAllUnselectedPricingDropdownOption === 3){
        //     this.proposalForm.controls.pm.setValue(true);
        //     this.proposalForm.controls.pm.disable();
        // }
    }

    private updatePricingBreakoutState(): void {
        const isPricingBreakout = this.isPricingBreakoutSelected();

        const breakoutControls: (keyof IProposalFormGroup)[] = [
            'materialDropdownOption', 'sAndH', 'warranty', 'useTax',
            'laborDropdownOption', 'pm', 'eng', 'ceng', 'eeng', 'leng', 'neng', 'qeng', 'grap', 'prog', 'dds', 'mech', 'spec', 'elec',
            'subcontractorsDropdownOption', 'otherWarranty', 'otherSAndH',
            'miscellaneousDropdownOption', 'tax',
            'travelDropdownOption',
            'showAllUnselectedPricing', 'showAllUnselectedPricingDropdownOption'
        ];

        breakoutControls.forEach(key => {
            const control = this.proposalForm.controls[key];
            isPricingBreakout ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });
        });

        if (isPricingBreakout) {
            this.updateMaterialCheckboxesState();
            this.updateLaborCheckboxesState();
            this.updateOtherCheckboxesState();
            this.updateShowAllUnselectedState();
            const estimateData = this.solPropertiesService.getEstimateData();
            const estimateSettings = this.solPropertiesService.getEstimateSettings();
            this.updateTaxCheckboxesBasedOnInstallAndTaxType(estimateSettings.installTypeID, estimateData.taxTypeID);
        }
    }

    private updateMaterialCheckboxesState(): void {
        const materialValue = this.proposalForm.controls.materialDropdownOption.value;
        const isNone = materialValue === 0;

        ['sAndH', 'warranty', 'useTax'].forEach(key => {
            const control = this.proposalForm.controls[key as keyof IProposalFormGroup] as FormControl<boolean>;
            if (isNone) {
                control.setValue(false, { emitEvent: false });
                control.disable({ emitEvent: false });
            } else {
                control.enable({ emitEvent: false });
            }
        });
    }

    private updateLaborCheckboxesState(preserveValues: boolean = true): void {
        const laborValue = this.proposalForm.controls.laborDropdownOption.value;
        const laborCheckboxKeys = ['pm', 'eng', 'ceng', 'eeng', 'leng', 'neng', 'qeng', 'grap', 'prog', 'dds', 'mech', 'spec', 'elec'];

        laborCheckboxKeys.forEach(key => {
            const control = this.proposalForm.controls[key as keyof IProposalFormGroup] as FormControl<boolean>;
            switch (laborValue) {
                case 0: // None
                    control.setValue(false, { emitEvent: false });
                    control.disable({ emitEvent: false });
                    break;
                case 1: // Summary Price
                    control.setValue(true, { emitEvent: false });
                    control.disable({ emitEvent: false });
                    break;
                case 2: // Show Details
                    if (!preserveValues) {
                        control.setValue(true, { emitEvent: false });
                    }
                    control.enable({ emitEvent: false });
                    break;
            }
        });
    }

    private updateOtherCheckboxesState(): void {
        const controls = this.proposalForm.controls;
        const isPricingBreakout = this.isPricingBreakoutSelected();

        if (controls.sAndH.value) {
            controls.otherSAndH.setValue(false, { emitEvent: false });
            controls.otherSAndH.disable({ emitEvent: false });
        } else {
            controls.otherSAndH.enable({ emitEvent: false });
        }

        if (controls.warranty.value) {
            controls.otherWarranty.setValue(false, { emitEvent: false });
            controls.otherWarranty.disable({ emitEvent: false });
        } else {
            controls.otherWarranty.enable({ emitEvent: false });
        }

        if (controls.useTax.value || !isPricingBreakout) {
            controls.tax.setValue(false, { emitEvent: false });
            controls.tax.disable({ emitEvent: false });
        } else {
            controls.tax.enable({ emitEvent: false });
        }
    }

    private updateShowAllUnselectedState(): void {
        const isChecked = this.proposalForm.controls.showAllUnselectedPricing.value;

        if (isChecked) {
            this.proposalForm.controls.showAllUnselectedPricingDropdownOption.enable({ emitEvent: false });
            this.updateUnselectedPricingCategoryState();
        } else {
            this.proposalForm.controls.showAllUnselectedPricingDropdownOption.disable({ emitEvent: false });
            this.resetUnselectedPricingOverrides();
        }
    }

    private updateUnselectedPricingCategoryState(): void {
        this.resetUnselectedPricingOverrides();
        const categoryId = this.proposalForm.controls.showAllUnselectedPricingDropdownOption.value;
        const controls = this.proposalForm.controls;

        switch (categoryId) {
            case 0: // Labor Summary
                controls.laborDropdownOption.setValue(1, { emitEvent: false });
                controls.laborDropdownOption.disable({ emitEvent: false });
                ['pm', 'eng', 'ceng', 'eeng', 'leng', 'neng', 'qeng', 'grap', 'prog', 'dds', 'mech', 'spec', 'elec'].forEach(key => {
                    const ctrl = controls[key as keyof IProposalFormGroup] as FormControl<boolean>;
                    ctrl.setValue(true, { emitEvent: false });
                    ctrl.disable({ emitEvent: false });
                });
                break;
            case 1: // Miscellaneous Costs
                controls.laborDropdownOption.enable({ emitEvent: false });
                controls.miscellaneousDropdownOption.setValue(1, { emitEvent: false });
                controls.miscellaneousDropdownOption.disable({ emitEvent: false });
                break;
            case 2: // Specialist Labor
                controls.laborDropdownOption.setValue(2, { emitEvent: false });
                controls.laborDropdownOption.disable({ emitEvent: false });
                this.updateLaborCheckboxesState();
                controls.spec.setValue(true, { emitEvent: false });
                controls.spec.disable({ emitEvent: false });
                break;
            case 3: // Project Management Labor
                controls.laborDropdownOption.setValue(2, { emitEvent: false });
                controls.laborDropdownOption.disable({ emitEvent: false });
                this.updateLaborCheckboxesState();
                controls.pm.setValue(true, { emitEvent: false });
                controls.pm.disable({ emitEvent: false });
                break;
            default:
                controls.laborDropdownOption.disable({ emitEvent: false });
                break;
        }
    }

    updateMaterialListState(): void {
        const isDisabled = this.proposalForm.controls.materialListOption.value === 0;
        const action = isDisabled ? 'disable' : 'enable';
        const options = { emitEvent: false };
        ['materialListIdentifier', 'sellPrice', 'genericName'].forEach(control => {
            this.proposalForm.controls[control as keyof IProposalFormGroup][action](options);
        });
    }

    private updateMaterialListOptions(areaPricingOption: number, emitEvent: boolean): void {
        const materialListOptions$ = areaPricingOption === AreaSummaryType.None
            ? this.solPropertiesService.getProposalMaterialListMasterData()
            : this.solPropertiesService.getProposalMaterialListByAreaMasterData();

        materialListOptions$
            .pipe(take(1))
            .subscribe(data => {
                this.materialListDropDownList = data;

                const selectedMaterialListOption = this.proposalForm.controls.materialListOption.value;
                const isValidSelection = data.some(option => option.id === selectedMaterialListOption);

                if (!isValidSelection && data.length > 0) {
                    this.proposalForm.controls.materialListOption.setValue(data[0].id, { emitEvent });
                }

                this.updateMaterialListState();
                this.syncProposalSettingsFromForm();
            });
    }

    private updateTaxCheckboxesBasedOnInstallAndTaxType(installType: number, taxTypeId: number): void {
        const isUserDefinedAndNoTax = installType === this.USER_DEFINED_INSTALL_TYPE_ID && 
                                      taxTypeId === this.NO_TAX_TYPE_ID;
        const isPricingBreakout = this.isPricingBreakoutSelected();

        if (isUserDefinedAndNoTax || !isPricingBreakout) {
            this.proposalForm.controls.useTax.disable({ emitEvent: false });
            this.proposalForm.controls.tax.disable({ emitEvent: false });
            if(taxTypeId === this.NO_TAX_TYPE_ID) {
                this.proposalForm.controls.useTax.setValue(false, { emitEvent: false });
                this.proposalForm.controls.tax.setValue(false, { emitEvent: false });
            }
        } else {
            this.proposalForm.controls.useTax.enable({ emitEvent: false });
            this.proposalForm.controls.tax.enable({ emitEvent: false });
        }
    }

    private isPricingBreakoutSelected(): boolean {
        return this.proposalForm.controls.projectPricingOption.value === PricingSummaryType.PricingBreakout
            || this.proposalForm.controls.areaPricingOption.value === AreaSummaryType.PricingBreakout;
    }

    private resetUnselectedPricingOverrides(): void {
        this.proposalForm.controls.laborDropdownOption.enable({ emitEvent: false });
        this.proposalForm.controls.miscellaneousDropdownOption.enable({ emitEvent: false });
        this.updateLaborCheckboxesState();
    }

    optionalPageSelection(){
        if (this.isReadOnly) {
            return;
        }

        const dialogRef= this.matDialog.open(SolPropertiesProposalOptionalPageSelectionComponent, {
            width: '600px',
            height: '428px',
            disableClose: true,
            data: this.solPropertiesService.getOtherMetaDataModel().proposalSettings.optionalPageSelection
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe((isDirty: boolean) => {
            if(isDirty){
                this.commonDataService.isSaveEnabled = true;
            }
        })
    }

    advancedOptions(){
        if (this.isReadOnly) {
            return;
        }

        const dialogRef= this.matDialog.open(SolPropertiesProposalAdvancedOptionsComponent, {
            width: '900px',
            disableClose: true,
            data: this.solPropertiesService.getOtherMetaDataModel().proposalSettings.advancedOptions
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe((isDirty: boolean) => {
            if(isDirty){
                this.commonDataService.isSaveEnabled = true;
            }
        });
    }
}